/**
 * Mock data for The Audax Group platform
 * Contains activations, events, products, and other content
 */

export interface Activation {
  id: string;
  title: string;
  brand: string;
  description: string;
  image: string;
  category: 'sports' | 'music' | 'fashion' | 'culture';
  metrics: {
    attendees?: number;
    socialImpressions?: number;
    brandAwarenessLift?: string;
  };
  story: string;
  location: string;
  date: string;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  image: string;
  location: string;
  address: string;
  date: string;
  time: string;
  status: 'upcoming' | 'happening-now' | 'completed';
  category: 'sports' | 'music' | 'fashion' | 'culture';
  readinessStatus: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: 'jerseys' | 'accessories' | 'collectibles';
  sizes?: string[];
  inStock: boolean;
}

export const activations: Activation[] = [
  {
    id: 'nike-community-court',
    title: 'Nike Community Court Activation',
    brand: 'Nike',
    description: 'Transforming urban spaces into cultural hubs through basketball',
    image: '/images/generated/activation-nike-community-court.png',
    category: 'sports',
    metrics: {
      attendees: 3500,
      socialImpressions: 2400000,
      brandAwarenessLift: '+47%',
    },
    story: 'We partnered with Nike to create an authentic community basketball experience that brought together local athletes, influencers, and culture creators. The activation featured custom court designs, live DJ sets, and exclusive merchandise drops, resulting in massive brand awareness lift and genuine community engagement.',
    location: 'Atlanta, GA',
    date: '2025-08',
  },
  {
    id: 'spotify-culture-sessions',
    title: 'Spotify Culture Sessions',
    brand: 'Spotify',
    description: 'Immersive music experiences celebrating emerging artists',
    image: '/images/generated/activation-spotify-culture-sessions.png',
    category: 'music',
    metrics: {
      attendees: 1800,
      socialImpressions: 5200000,
      brandAwarenessLift: '+62%',
    },
    story: 'This multi-day activation showcased Spotify\'s commitment to emerging artists with live performances, artist meet-and-greets, and interactive listening experiences. The bold visual identity and intimate venue created unforgettable moments that drove massive social engagement.',
    location: 'Atlanta, GA',
    date: '2025-09',
  },
  {
    id: 'atlanta-soccer-fan-zone',
    title: 'Atlanta Soccer Fan Zone - World Cup 2026',
    brand: 'Atlanta United / World Cup 2026',
    description: 'Pre-World Cup activation celebrating soccer culture in Atlanta',
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'sports',
    metrics: {
      attendees: 8500,
      socialImpressions: 3800000,
      brandAwarenessLift: '+71%',
    },
    story: 'As Atlanta prepares to host World Cup 2026 matches, we created an immersive fan zone experience featuring live match screenings, interactive soccer challenges, exclusive merchandise, and appearances by soccer legends. The activation established Atlanta as a premier soccer destination.',
    location: 'Atlanta, GA',
    date: '2025-11',
  },
  {
    id: 'adidas-originals-popup',
    title: 'Adidas Originals Pop-Up',
    brand: 'Adidas',
    description: 'Limited-edition sneaker drops with cultural storytelling',
    image: '/images/generated/hero-experiential-activation-crowd.png',
    category: 'fashion',
    metrics: {
      attendees: 2200,
      socialImpressions: 1900000,
      brandAwarenessLift: '+54%',
    },
    story: 'A three-day pop-up experience celebrating Adidas Originals\' cultural impact through exclusive sneaker releases, customization stations, and collaborations with local artists. The activation sold out within hours and generated massive social buzz.',
    location: 'Atlanta, GA',
    date: '2025-07',
  },
  {
    id: 'redbull-music-academy',
    title: 'Red Bull Music Academy',
    brand: 'Red Bull',
    description: 'Empowering the next generation of music producers',
    image: '/images/generated/brand-partnership-meeting.png',
    category: 'music',
    metrics: {
      attendees: 950,
      socialImpressions: 1500000,
      brandAwarenessLift: '+43%',
    },
    story: 'An intimate workshop series bringing together emerging producers with industry legends for hands-on learning, live production sessions, and collaborative music creation. The activation positioned Red Bull as a champion of music education and culture.',
    location: 'Atlanta, GA',
    date: '2025-10',
  },
  {
    id: 'supreme-atlanta-drop',
    title: 'Supreme Atlanta Exclusive Drop',
    brand: 'Supreme',
    description: 'Cultural moment celebrating streetwear authenticity',
    image: '/images/generated/hero-experiential-activation-crowd.png',
    category: 'fashion',
    metrics: {
      attendees: 4200,
      socialImpressions: 6700000,
      brandAwarenessLift: '+89%',
    },
    story: 'A one-day exclusive merchandise drop celebrating Supreme\'s cultural impact with Atlanta-specific designs, live performances, and surprise appearances. Lines wrapped around the block, creating a genuine cultural moment that dominated social media.',
    location: 'Atlanta, GA',
    date: '2025-06',
  },
];

export const events: Event[] = [
  {
    id: 'world-cup-kickoff-party',
    title: 'World Cup 2026 Kickoff Party',
    description: 'Celebrate the start of World Cup 2026 with live screenings, music, and exclusive merch',
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    location: 'Audax Arena',
    address: '123 Peachtree St NE, Atlanta, GA 30303',
    date: '2026-06-11',
    time: '2:00 PM - 11:00 PM',
    status: 'upcoming',
    category: 'sports',
    readinessStatus: 'Venue confirmed, tickets available',
  },
  {
    id: 'nike-air-launch',
    title: 'Nike Air Max Launch Experience',
    description: 'Be the first to experience the new Nike Air Max collection',
    image: '/images/generated/activation-nike-community-court.png',
    location: 'Ponce City Market',
    address: '675 Ponce De Leon Ave NE, Atlanta, GA 30308',
    date: '2026-02-15',
    time: '10:00 AM - 8:00 PM',
    status: 'upcoming',
    category: 'fashion',
    readinessStatus: 'Early access registration open',
  },
  {
    id: 'spotify-live-sessions',
    title: 'Spotify Live Sessions - Atlanta Edition',
    description: 'Intimate performances by Atlanta\'s rising stars',
    image: '/images/generated/activation-spotify-culture-sessions.png',
    location: 'The Eastern',
    address: '777 Memorial Dr SE, Atlanta, GA 30316',
    date: '2026-03-20',
    time: '7:00 PM - 11:00 PM',
    status: 'upcoming',
    category: 'music',
    readinessStatus: 'Limited tickets remaining',
  },
  {
    id: 'atlanta-united-fanfest',
    title: 'Atlanta United Season Opener Fan Fest',
    description: 'Kick off the MLS season with the Five Stripes',
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    location: 'Mercedes-Benz Stadium Plaza',
    address: '1 AMB Dr NW, Atlanta, GA 30313',
    date: '2026-02-28',
    time: '12:00 PM - 6:00 PM',
    status: 'upcoming',
    category: 'sports',
    readinessStatus: 'Free entry, RSVP recommended',
  },
  {
    id: 'adidas-customization-lab',
    title: 'Adidas Customization Lab',
    description: 'Design your own custom Adidas gear with professional artists',
    image: '/images/generated/hero-experiential-activation-crowd.png',
    location: 'Lenox Square',
    address: '3393 Peachtree Rd NE, Atlanta, GA 30326',
    date: '2026-04-10',
    time: '11:00 AM - 7:00 PM',
    status: 'upcoming',
    category: 'fashion',
    readinessStatus: 'Booking slots available',
  },
  {
    id: 'culture-summit-2026',
    title: 'The Audax Culture Summit 2026',
    description: 'Annual gathering of brands, creators, and culture leaders',
    image: '/images/generated/brand-partnership-meeting.png',
    location: 'Georgia World Congress Center',
    address: '285 Andrew Young International Blvd NW, Atlanta, GA 30313',
    date: '2026-05-15',
    time: '9:00 AM - 6:00 PM',
    status: 'upcoming',
    category: 'culture',
    readinessStatus: 'Professional tickets on sale',
  },
  {
    id: 'world-cup-watch-party-1',
    title: 'World Cup 2026 - USA vs Mexico Watch Party',
    description: 'Experience the rivalry with thousands of passionate fans',
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    location: 'Audax Arena',
    address: '123 Peachtree St NE, Atlanta, GA 30303',
    date: '2026-06-20',
    time: '5:00 PM - 10:00 PM',
    status: 'upcoming',
    category: 'sports',
    readinessStatus: 'VIP packages available',
  },
  {
    id: 'summer-music-festival',
    title: 'Summer Sounds Festival',
    description: 'Three days of music, art, and culture',
    image: '/images/generated/activation-spotify-culture-sessions.png',
    location: 'Piedmont Park',
    address: '1071 Piedmont Ave NE, Atlanta, GA 30309',
    date: '2026-07-12',
    time: '2:00 PM - 11:00 PM',
    status: 'upcoming',
    category: 'music',
    readinessStatus: '3-day passes on sale',
  },
];

export const products: Product[] = [
  {
    id: 'atlanta-2026-home-jersey',
    name: 'Atlanta 2026 Home Jersey',
    description: 'Official World Cup 2026 Atlanta commemorative jersey. Premium quality with moisture-wicking fabric.',
    price: 89.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'jerseys',
    sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
  },
  {
    id: 'atlanta-2026-away-jersey',
    name: 'Atlanta 2026 Away Jersey',
    description: 'Sleek black away jersey with gold accents celebrating Atlanta\'s World Cup hosting.',
    price: 89.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'jerseys',
    sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
  },
  {
    id: 'five-stripes-scarf',
    name: 'Atlanta Five Stripes Scarf',
    description: 'Classic supporter scarf in Atlanta United colors with World Cup 2026 branding.',
    price: 29.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'accessories',
    inStock: true,
  },
  {
    id: 'world-cup-atlanta-hat',
    name: 'World Cup 2026 Atlanta Cap',
    description: 'Adjustable snapback cap featuring embroidered World Cup 2026 and Atlanta branding.',
    price: 34.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'accessories',
    inStock: true,
  },
  {
    id: 'atlanta-soccer-hoodie',
    name: 'Atlanta Soccer Hoodie',
    description: 'Premium heavyweight hoodie with bold World Cup 2026 graphics.',
    price: 69.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'jerseys',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
  },
  {
    id: 'limited-edition-ball',
    name: 'Limited Edition Match Ball',
    description: 'Official World Cup 2026 replica ball with Atlanta host city design. Numbered limited edition.',
    price: 149.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'collectibles',
    inStock: true,
  },
  {
    id: 'atlanta-supporter-jacket',
    name: 'Atlanta Supporter Jacket',
    description: 'Water-resistant windbreaker with bold graphics and hidden pockets.',
    price: 79.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'jerseys',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
  },
  {
    id: 'world-cup-pin-set',
    name: 'World Cup 2026 Pin Collection',
    description: 'Collector\'s set of 6 enamel pins featuring Atlanta and World Cup 2026 designs.',
    price: 24.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'collectibles',
    inStock: true,
  },
  {
    id: 'atlanta-training-jersey',
    name: 'Atlanta Training Jersey',
    description: 'Professional-grade training jersey with breathable mesh panels.',
    price: 64.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'jerseys',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
  },
  {
    id: 'world-cup-backpack',
    name: 'World Cup 2026 Backpack',
    description: 'Durable backpack with laptop sleeve and Atlanta World Cup 2026 embroidery.',
    price: 54.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'accessories',
    inStock: true,
  },
  {
    id: 'atlanta-beanie',
    name: 'Atlanta Soccer Beanie',
    description: 'Knit beanie with embroidered Atlanta and World Cup logos.',
    price: 24.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'accessories',
    inStock: true,
  },
  {
    id: 'signed-poster',
    name: 'Signed Limited Edition Poster',
    description: 'Hand-numbered poster signed by Atlanta soccer legends. Only 100 available.',
    price: 199.99,
    image: '/images/generated/activation-atlanta-soccer-fan-zone.png',
    category: 'collectibles',
    inStock: false,
  },
];

export const brandMetrics = {
  totalActivations: 47,
  totalAttendees: 125000,
  avgBrandLift: '+58%',
  socialReach: '42M+',
  partnerBrands: ['Nike', 'Adidas', 'Spotify', 'Red Bull', 'Supreme', 'Coca-Cola', 'Meta', 'Amazon'],
};
